import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Phone, Mail, MapPin, Facebook, Twitter, Linkedin, Instagram } from 'lucide-react';

const Footer = ({ onQuoteClick }) => {
  const currentYear = new Date().getFullYear();
  const location = useLocation();
  const navigate = useNavigate();

  // Scroll to a homepage section, navigating to "/" first if we're not already there
  const goToHomeSection = (sectionId) => {
    if (location.pathname === '/') {
      const el = document.getElementById(sectionId);
      if (el) el.scrollIntoView({ behavior: 'smooth' });
    } else {
      navigate(`/#${sectionId}`);
      // Give the homepage time to mount (video, hero, grids) before scrolling
      setTimeout(() => {
        const el = document.getElementById(sectionId);
        if (el) el.scrollIntoView({ behavior: 'smooth' });
      }, 500);
    }
  };

  return (
    <footer className="bg-gray-900 text-white">
      {/* Main Footer Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Company Info */}
          <div>
            <img 
              src="/logo.png" 
              alt="Cooper Equipment Rentals" 
              className="h-14 w-auto mb-4"
            />
            <p className="text-gray-400 mb-4">
              Service and customer satisfaction is our main goal. Serving Orange County, Inland Empire, and Los Angeles County with over 400 electric scissor lifts.
            </p>
            <div className="flex space-x-4">
              <a href="#" className="text-gray-400 hover:text-orange-600 transition-colors">
                <Facebook className="w-5 h-5" />
              </a>
              <a href="#" className="text-gray-400 hover:text-orange-600 transition-colors">
                <Twitter className="w-5 h-5" />
              </a>
              <a href="#" className="text-gray-400 hover:text-orange-600 transition-colors">
                <Linkedin className="w-5 h-5" />
              </a>
              <a href="#" className="text-gray-400 hover:text-orange-600 transition-colors">
                <Instagram className="w-5 h-5" />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-lg font-semibold mb-4">Quick Links</h3>
            <ul className="space-y-2">
              <li>
                <Link
                  to="/equipment"
                  className="text-gray-400 hover:text-orange-600 transition-colors"
                  data-testid="footer-quick-equipment"
                >
                  Equipment
                </Link>
              </li>
              <li>
                <button
                  onClick={() => goToHomeSection('why-choose-us')}
                  className="text-gray-400 hover:text-orange-600 transition-colors text-left"
                  data-testid="footer-quick-why-choose-us"
                >
                  Why Choose Us
                </button>
              </li>
              <li>
                <button
                  onClick={() => goToHomeSection('pricing')}
                  className="text-gray-400 hover:text-orange-600 transition-colors text-left"
                  data-testid="footer-quick-pricing"
                >
                  Pricing
                </button>
              </li>
              <li>
                <button
                  onClick={onQuoteClick}
                  className="text-gray-400 hover:text-orange-600 transition-colors text-left"
                  data-testid="footer-quick-request-quote"
                >
                  Request Quote
                </button>
              </li>
            </ul>
          </div>

          {/* Equipment Categories */}
          <div>
            <h3 className="text-lg font-semibold mb-4">Equipment</h3>
            <ul className="space-y-2">
              <li>
                <Link
                  to="/equipment?category=Scissor%20Lift"
                  className="text-gray-400 hover:text-orange-600 transition-colors"
                  data-testid="footer-category-scissor-lift"
                >
                  Scissor Lifts
                </Link>
              </li>
              <li>
                <Link
                  to="/equipment?category=Boom%20Lift"
                  className="text-gray-400 hover:text-orange-600 transition-colors"
                  data-testid="footer-category-boom-lift"
                >
                  Boom Lifts
                </Link>
              </li>
              <li>
                <Link
                  to="/equipment?category=Telehandler"
                  className="text-gray-400 hover:text-orange-600 transition-colors"
                  data-testid="footer-category-telehandler"
                >
                  Telehandlers
                </Link>
              </li>
              <li>
                <Link
                  to="/equipment?category=Fork%20Lift"
                  className="text-gray-400 hover:text-orange-600 transition-colors"
                  data-testid="footer-category-fork-lift"
                >
                  Forklifts
                </Link>
              </li>
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h3 className="text-lg font-semibold mb-4">Contact Us</h3>
            <ul className="space-y-3">
              <li>
                <a href="tel:7143795545" className="flex items-center text-gray-400 hover:text-orange-600 transition-colors">
                  <Phone className="w-5 h-5 mr-2" />
                  (714) 379-5545
                </a>
              </li>
              <li>
                <a href="mailto:info@cooperequipmentrentals.com" className="flex items-center text-gray-400 hover:text-orange-600 transition-colors break-all">
                  <Mail className="w-5 h-5 mr-2 flex-shrink-0" />
                  info@cooperequipmentrentals.com
                </a>
              </li>
              <li className="flex items-start text-gray-400">
                <MapPin className="w-5 h-5 mr-2 mt-1 flex-shrink-0" />
                <span>9629 Beverly Rd, Pico Rivera, CA 90660</span>
              </li>
            </ul>
            <Button 
              onClick={onQuoteClick}
              className="mt-6 bg-orange-600 hover:bg-orange-700 text-white w-full"
            >
              Get a Quote
            </Button>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="md:flex md:items-center md:justify-between">
            <p className="text-gray-400 text-sm text-center md:text-left">
              &copy; {currentYear} Cooper Equipment Rentals. All rights reserved.
            </p>
            <div className="mt-4 md:mt-0 flex justify-center space-x-6">
              <a href="#" className="text-gray-400 hover:text-orange-600 text-sm transition-colors">
                Privacy Policy
              </a>
              <a href="#" className="text-gray-400 hover:text-orange-600 text-sm transition-colors">
                Terms of Service
              </a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
